import React, { useState, CSSProperties, useEffect } from 'react';
import classnames from 'classnames';

import { Spinner, SpinnerWithLabel } from 'components/Spinner';
import { CreditCard } from 'components/payments/CreditCard';
import { usePaymentMethods } from 'features/stripe/hooks/usePaymentMethods';
import { AddCardButton } from 'components/payments/AddCardButton';
import { PaymentElementForm } from 'components/payments/PaymentElementForm';
import { Alert } from 'components/UI/Alert';

import { srollToNewCard } from './helpers';
import { PaymentItem } from './PaymentItem';
import { PaymentItemPayPal } from './PaymentItemPayPal';
import { getErrorMessage } from 'errors';
import * as Types from 'types';

type Props = {
  userId: number;
  payUsingPayPalUrl?: string;
  returnUrl: string;
  className?: string;
  classNameTitle?: string;
  classNameNewCard?: string;
  style?: CSSProperties;
  onContinueUsingPayment: (paymentMethodId: string) => Promise<Types.ApiResult<{}>>;
  onSubmittingStateChange?: (isSubmitting: boolean) => void;
};

export const ChoosePaymentMethod: React.FC<Props> = ({
  userId,
  payUsingPayPalUrl,
  returnUrl,
  className,
  classNameTitle,
  classNameNewCard,
  children,
  style,
  onContinueUsingPayment,
  onSubmittingStateChange,
}) => {
  const [submittingPaymentMethodId, setSubmittingPaymentMethodId] = useState<string | undefined>();
  const [isAddingNewCard, setIsAddingNewCard] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const { paymentMethodsState, paymentMethods } = usePaymentMethods(userId);

  const continueUsingPaymentMethod = async (paymentMethodId: string) => {
    if (submittingPaymentMethodId) {
      return;
    }
    setError(undefined);
    setSubmittingPaymentMethodId(paymentMethodId);

    try {
      const { error } = await onContinueUsingPayment(paymentMethodId);
      if (error) {
        throw error;
      }
    } catch (e) {
      const message = getErrorMessage(e);
      bugsnag.notify(message);
      setError(message);
      setSubmittingPaymentMethodId(undefined);
      setIsAddingNewCard(false);
    }
  };

  useEffect(() => {
    onSubmittingStateChange?.(submittingPaymentMethodId !== undefined);
  }, [submittingPaymentMethodId]);

  if (paymentMethodsState === 'initial-fetch') {
    return (
      <div className='h-100 d-flex justify-content-center align-items-center' style={style}>
        <SpinnerWithLabel>Loading payment options</SpinnerWithLabel>
      </div>
    );
  }

  const addingCardIsTheOnlyOption =
    paymentMethodsState === 'ready' && paymentMethods.length === 0 && !payUsingPayPalUrl;

  const title = addingCardIsTheOnlyOption ? 'Add payment method' : 'Choose payment method';

  return (
    <div
      className={classnames(className, {
        'choose-payment-method--is-adding-new-card': isAddingNewCard,
      })}
      style={style}
    >
      <h1 className={classnames('headline headline--small', classNameTitle)}>
        {title}
        {submittingPaymentMethodId && <Spinner className='d-inline-block ml-1 pos-abs' />}
      </h1>
      {error && (
        <Alert className='mb-2' variant='error'>
          {error}
        </Alert>
      )}
      {children}
      {!addingCardIsTheOnlyOption && (
        <div className='row justify-content-center nmb-2'>
          {paymentMethods?.map(({ id, card }) => (
            <PaymentItem key={id} id={id} selectedId={submittingPaymentMethodId}>
              <CreditCard
                onClick={() => {
                  setIsAddingNewCard(false);
                  continueUsingPaymentMethod(id);
                }}
                cc={card}
                style={{ maxWidth: 300 }}
                isSelected={submittingPaymentMethodId === id}
              />
            </PaymentItem>
          ))}
          {payUsingPayPalUrl && (
            <PaymentItemPayPal
              selectedId={submittingPaymentMethodId}
              onSelected={(paymentMethodId) => {
                setIsAddingNewCard(false);
                setSubmittingPaymentMethodId(paymentMethodId);
              }}
              url={payUsingPayPalUrl}
            />
          )}
          <PaymentItem id='add-credit-card' selectedId={submittingPaymentMethodId}>
            <AddCardButton
              onClick={() => {
                setIsAddingNewCard(true);
                setTimeout(() => {
                  srollToNewCard();
                }, 1);
              }}
            />
          </PaymentItem>
        </div>
      )}
      {paymentMethods.length === 0 && (
        <div className='muted mx-auto text-center my-1' style={{ maxWidth: 580 }}>
          We have recently changed our payment processor. Your previous payment methods were
          deleted. Please enter them again. We are now supporting more credit cards, including ones
          protected by 3D Secure.
        </div>
      )}
      <div className='mx-auto' style={{ maxWidth: 580 }}>
        {(isAddingNewCard || addingCardIsTheOnlyOption) && (
          <div className={classnames('pos-rel', classNameNewCard)} id='add-new-payment-method'>
            {!addingCardIsTheOnlyOption && (
              <div>
                <div className='labeled-separator muted my-2'>
                  <div className='labeled-separator__content'>or enter new credit card</div>
                </div>
              </div>
            )}
            <PaymentElementForm returnUrl={returnUrl} onSuccess={continueUsingPaymentMethod} />
          </div>
        )}
      </div>
    </div>
  );
};
